// src/DocumentForm.js
import React, { useState, useEffect, useCallback } from 'react'; // (เพิ่ม useCallback)
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import CreatableSelect from 'react-select/creatable';
import { useAuth } from './AuthContext'; // (1. Import useAuth)

const API_URL = 'https://rys-backend.onrender.com/api';

// --- (2. ลบ getAuthHeaders เก่าทิ้ง) ---

// (สไตล์สำหรับ react-select - เหมือนเดิม)
const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    borderColor: 'rgb(209 213 219)',
    borderRadius: '0.375rem',
    minHeight: '46px',
  }),
  input: (provided) => ({ ...provided, margin: '0', padding: '0' }),
  valueContainer: (provided) => ({ ...provided, padding: '0 6px' }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(219 234 254)', // bg-blue-100
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: 'rgb(30 64 175)', // text-blue-800
  }),
};

function DocumentForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  // --- (3. ดึง getAuthHeaders จาก useAuth) ---
  const { getAuthHeaders } = useAuth();

  // --- States for Form ---
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [googleDriveLink, setGoogleDriveLink] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  
  // --- States for Dropdowns ---
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  const [tagOptions, setTagOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // (4. แก้ไข fetchData)
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const authHeaders = getAuthHeaders(); // (เรียกใช้)
      
      const [categoriesRes, tagsRes] = await Promise.all([
        axios.get(`${API_URL}/categories`, authHeaders), 
        axios.get(`${API_URL}/tags`, authHeaders)       
      ]);
      
      setCategories(categoriesRes.data);
      setTagOptions(tagsRes.data); 

      if (isEditing) {
        const docRes = await axios.get(`${API_URL}/document/${id}`, authHeaders); 
        const doc = docRes.data;
        setTitle(doc.title);
        setDescription(doc.description);
        setGoogleDriveLink(doc.google_drive_link);
        setIsPinned(doc.is_pinned);
        setSelectedCategory(doc.category_id);
        setSelectedTags(doc.tags.map(tag => ({ label: tag, value: tag })));
      }
      
    } catch (err) {
      toast.error("ไม่สามารถโหลดข้อมูล Categories หรือ Tags ได้");
    } finally {
      setIsLoading(false);
    }
  }, [id, isEditing, getAuthHeaders]); // (5. เพิ่ม getAuthHeaders ใน dependency)

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !googleDriveLink) {
      toast.error("กรุณากรอกหัวข้อ และ Google Drive Link");
      return;
    }
    setIsSubmitting(true);
    
    const payload = {
      title,
      description,
      google_drive_link: googleDriveLink,
      category_id: selectedCategory,
      is_pinned: isPinned,
      tags: selectedTags.map(tag => tag.value) // (แปลงกลับเป็น array string)
    };

    try {
      const method = isEditing ? 'put' : 'post';
      const url = isEditing 
        ? `${API_URL}/documents/${id}` 
        : `${API_URL}/documents`;   
      
      // (6. เรียกใช้ getAuthHeaders() ใน handleSubmit)
      const res = await axios({ method, url, data: payload, ...getAuthHeaders() }); 
      
      toast.success(res.data.message);
      navigate('/admin/documents/all'); 
      
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ดำเนินการไม่สำเร็จ');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="container mx-auto p-8 text-center">กำลังโหลดข้อมูลฟอร์ม...</div>;
  }
  
  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-xl max-w-4xl mx-auto">
        
        <h2 className="text-3xl font-semibold text-gray-800 mb-6 border-b pb-3">
          {isEditing ? 'แก้ไขเอกสาร' : 'เพิ่มเอกสารใหม่'}
        </h2>
        
        <div className="space-y-6">
          {/* (Input: Title) */}
          <div>
            <label htmlFor="title" className="block text-base font-medium text-gray-700">หัวข้อ *</label>
            <input
              type="text" id="title" value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
            />
          </div>

          {/* (Input: Description) */}
          <div>
            <label htmlFor="description" className="block text-base font-medium text-gray-700">รายละเอียด</label>
            <textarea
              id="description" value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
            />
          </div>

          {/* (Input: Google Drive Link) */}
          <div>
            <label htmlFor="google_drive_link" className="block text-base font-medium text-gray-700">Google Drive Link *</label>
            <input
              type="url" id="google_drive_link" value={googleDriveLink}
              onChange={(e) => setGoogleDriveLink(e.target.value)}
              required
              className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
            />
          </div>

          {/* (Dropdown: Category) */}
          <div>
            <label htmlFor="category_id" className="block text-base font-medium text-gray-700">หมวดหมู่</label>
            <select
              id="category_id"
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm bg-white"
            >
              <option value="">-- ไม่ระบุ --</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* (Dropdown: Tags) */}
          <div>
            <label htmlFor="tags" className="block text-base font-medium text-gray-700">Tags (พิมพ์เพื่อเพิ่มใหม่)</label>
            <CreatableSelect
              isMulti
              id="tags"
              instanceId="tags-select"
              value={selectedTags}
              onChange={setSelectedTags}
              options={tagOptions} 
              placeholder="เลือก Tag ที่มีอยู่ หรือพิมพ์เพื่อสร้างใหม่..."
              className="mt-1 text-base"
              styles={customSelectStyles}
            />
          </div>

          {/* (Checkbox ปักหมุด) */}
          <div className="flex items-center">
            <input
              id="is_pinned"
              name="is_pinned"
              type="checkbox"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_pinned" className="ml-2 block text-base font-medium text-gray-900">
              ปักหมุด (แสดงเป็นเอกสารสำคัญ)
            </label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 p-6 bg-gray-50 rounded-b-lg mt-8 -mx-8 -mb-8">
          {/* (Buttons: ยกเลิก, บันทึก) */}
          <button type="button" onClick={() => navigate(-1)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300">
            ยกเลิก
          </button>
          <button 
            type="submit" 
            className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300 disabled:bg-gray-400"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'กำลังบันทึก...' : (isEditing ? 'บันทึกการแก้ไข' : 'สร้างเอกสาร')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DocumentForm;